/**
 * ZButton — Zephyr OS Design System
 *
 * Variants: primary, secondary, minimal, destructive, cinematic
 * Each variant matches design-system.md Section 7 exactly.
 */

import { forwardRef } from 'react';

const baseStyles = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  cursor: 'pointer',
  border: 'none',
  outline: 'none',
  transition: 'all 150ms ease',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  fontFeatureSettings: '"cv01", "ss03"',
};

const variants = {
  primary: (accent) => ({
    ...baseStyles,
    background: accent || '#6B35B8',
    color: '#ffffff',
    padding: '10px 20px',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: '0.3px',
  }),
  secondary: () => ({
    ...baseStyles,
    background: 'rgba(255,255,255,0.04)',
    color: '#b0b4c0',
    padding: '10px 20px',
    borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.08)',
    fontSize: 14,
    fontWeight: 500,
  }),
  minimal: () => ({
    ...baseStyles,
    background: 'transparent',
    color: '#6b7084',
    padding: '8px 16px',
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 500,
  }),
  destructive: () => ({
    ...baseStyles,
    background: '#E24B4A',
    color: '#ffffff',
    padding: '10px 20px',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
  }),
  cinematic: () => ({
    ...baseStyles,
    background: 'rgba(255,255,255,0.10)',
    color: '#f0f0f5',
    padding: '14px 28px',
    borderRadius: 32,
    border: '1px solid rgba(255,255,255,0.30)',
    fontSize: 12,
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '1px',
  }),
};

const hoverStyles = {
  primary: { opacity: 0.85 },
  secondary: { background: 'rgba(255,255,255,0.06)', color: '#f0f0f5', borderColor: 'rgba(255,255,255,0.12)' },
  minimal: { color: '#b0b4c0' },
  destructive: { opacity: 0.85 },
  cinematic: { background: 'rgba(255,255,255,0.15)' },
};

const ZButton = forwardRef(function ZButton(
  { variant = 'primary', accent, children, onClick, disabled, className = '', style = {}, ...rest },
  ref
) {
  const getVariantStyle = variants[variant] || variants.primary;
  const variantStyle = getVariantStyle(accent);

  const handleMouseEnter = (e) => {
    if (disabled) return;
    const hover = hoverStyles[variant] || {};
    Object.assign(e.currentTarget.style, hover);
  };

  const handleMouseLeave = (e) => {
    if (disabled) return;
    const base = getVariantStyle(accent);
    // Reset hover-affected properties
    if (variant === 'primary' || variant === 'destructive') {
      e.currentTarget.style.opacity = '1';
    } else if (variant === 'secondary') {
      e.currentTarget.style.background = base.background;
      e.currentTarget.style.color = base.color;
      e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
    } else if (variant === 'minimal') {
      e.currentTarget.style.color = base.color;
    } else if (variant === 'cinematic') {
      e.currentTarget.style.background = base.background;
    }
  };

  const handleMouseDown = (e) => {
    if (disabled) return;
    if (variant === 'primary' || variant === 'destructive') {
      e.currentTarget.style.transform = 'scale(0.98)';
    }
  };

  const handleMouseUp = (e) => {
    e.currentTarget.style.transform = 'none';
  };

  return (
    <button
      ref={ref}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      disabled={disabled}
      className={className}
      style={{
        ...variantStyle,
        ...style,
        opacity: disabled ? 0.3 : style.opacity || 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      {...rest}
    >
      {children}
    </button>
  );
});

export default ZButton;
